import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { filterLongCategoryName, isLongCategoryName } from "@faire/retailer-visitor-shared/components/Carousel/categoryTileUtils";
import { GhostBlock } from "@faire/retailer-visitor-shared/components/GhostLoading/GhostBlock";
import { GridViewBase } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/ByType/GridViewBase";
import { CarouselBase } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/__internal__/Context";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { TransparencyOverlay } from "@faire/web--source/ui/TransparencyOverlay";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import * as React from "react";
import styled from "styled-components";
var IMAGE_WIDTH = 72;
var TILE_WIDTH = 88;
export var AvatarCarousel = function(props) {
    var CarouselBaseComponent = CarouselBase;
    var _React_useContext = React.useContext(Context), onItemClick = _React_useContext.onItemClick, layoutElement = _React_useContext.layoutElement, requestId = _React_useContext.requestId, onVisible = _React_useContext.onVisible;
    var isMobile = useViewport().isMobile;
    var renderAvatarTile = function(avatarTile, index) {
        return /*#__PURE__*/ _jsx(AvatarTile, {
            avatar: avatarTile,
            eventName: layoutElement.on_click_event_name,
            requestId: requestId,
            onItemClick: function(categoryLink) {
                return onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(categoryLink, "", "");
            }
        }, index);
    };
    var getItems = function() {
        return layoutElement.layout_element_data.map(function(layoutElementData) {
            return layoutElementData["category_tile"];
        }).filter(isNotUndefined);
    };
    var onItemsVisible = function(items) {
        var itemTokens = items.map(function(item) {
            return item.category_path_identifier;
        }).filter(isNotUndefined);
        if (layoutElement.on_click_event_name) {
            var _itemTokens_join;
            onVisible === null || onVisible === void 0 ? void 0 : onVisible(layoutElement.on_click_event_name, requestId, (_itemTokens_join = itemTokens === null || itemTokens === void 0 ? void 0 : itemTokens.join(" ")) !== null && _itemTokens_join !== void 0 ? _itemTokens_join : "");
        }
    };
    return isMobile && layoutElement.type === ILayoutElement.Type.AVATAR_CAROUSEL_WITH_GRID ? /*#__PURE__*/ _jsx(StyledGridViewBase, {
        children: /*#__PURE__*/ _jsx(GridViewBase, {
            items: getItems(),
            renderItem: renderAvatarTile,
            rows: 2,
            itemsPerSlide: 3,
            gap: 24,
            expandable: true,
            useArrowToggle: true
        })
    }) : /*#__PURE__*/ _jsx(CarouselBaseComponent, _object_spread_props(_object_spread({}, props), {
        onItemsVisible: onItemsVisible,
        items: getItems(),
        renderItem: renderAvatarTile,
        itemGap: 16,
        noWrap: true
    }));
};
var AvatarTile = function(param) {
    var avatar = param.avatar, onItemClick = param.onItemClick;
    var isSSR = useIsSSR();
    var imageURL = avatar.image ? optimizeImageUrlFillSquare(avatar.image, isSSR, IMAGE_WIDTH) : EAEAEA_PIXEL;
    var ctaLink = avatar.cta_link;
    if (!ctaLink) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Column, {
        align: "center",
        style: {
            width: TILE_WIDTH,
            margin: "0 auto"
        },
        children: /*#__PURE__*/ _jsxs(StyledLink, {
            to: ctaLink,
            onClick: function() {
                return onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick(ctaLink);
            },
            "data-test-id": "avatar-tile-link",
            children: [
                /*#__PURE__*/ _jsxs(ImageContainer, {
                    children: [
                        /*#__PURE__*/ _jsx(StyledImageOverlay, {}),
                        !avatar.image ? /*#__PURE__*/ _jsx(GhostImage, {}) : /*#__PURE__*/ _jsx(Image, {
                            alt: avatar.name,
                            src: imageURL
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    size: 4
                }),
                /*#__PURE__*/ _jsx(AvatarName, {
                    $isLongCategoryName: isLongCategoryName(avatar.name),
                    children: filterLongCategoryName(avatar.name)
                })
            ]
        })
    });
};
var ImageContainer = styled.div.withConfig({
    componentId: "sc-8f800a74-0"
})([
    "position:relative;width:",
    "px;height:",
    "px;margin:auto;"
], IMAGE_WIDTH, IMAGE_WIDTH);
var StyledImageOverlay = styled(TransparencyOverlay).withConfig({
    componentId: "sc-8f800a74-1"
})([
    "border:1px solid ",
    ";border-radius:100%;"
], Core.surface.muted);
var Image = styled.img.withConfig({
    componentId: "sc-8f800a74-2"
})([
    "position:absolute;top:0;width:100%;height:100%;object-fit:cover;object-position:center;border-radius:100%;"
]);
var GhostImage = styled(GhostBlock).withConfig({
    componentId: "sc-8f800a74-3"
})([
    "border-radius:100%;"
]);
var AvatarName = styled(Typography).attrs({
    align: "center",
    maxLines: 3,
    forwardedAs: "p"
}).withConfig({
    componentId: "sc-8f800a74-4"
})([
    "&&&{margin-top:",
    ";",
    "{margin-bottom:",
    ";}max-height:60px;width:",
    "px;text-overflow:ellipsis;display:",
    ";overflow:hidden;text-align:center;}"
], getSpacing("1x"), responsiveMediaQueries.mobile, getSpacing("1x"), TILE_WIDTH, function(props) {
    return props.$isLongCategoryName ? "-webkit-box" : "inline-block";
});
var StyledGridViewBase = styled.div.withConfig({
    componentId: "sc-8f800a74-5"
})([
    "",
    "{margin:",
    ";}"
], responsiveMediaQueries.mobile, getSpacing(0, "-2x"));
var StyledLink = styled(Link).withConfig({
    componentId: "sc-8f800a74-6"
})([
    "&:focus-visible{outline:solid;outline:revert;outline-offset:-1px;}"
]);
